import {
	canWaterWidgetsShareSpace,
	WATER_NETWORK_FREEFORM_SEARCH_STEP,
} from '../../../utils/waterNetworkLayout';
import { STATUS_ARROW_WIDGET_TYPE } from '../../../widgets/assetStatusUtils';

export const getDuplicatedWidgetSize = ({
	rawWidth,
	rawHeight,
	minimumWidgetSize,
	isFreeformLayoutWidget,
	defaultDivWidth,
	defaultDivHeight,
	gap,
}) => {
	const normalizedWidth = Math.max(minimumWidgetSize.width, rawWidth);
	const normalizedHeight = Math.max(minimumWidgetSize.height, rawHeight);

	if (isFreeformLayoutWidget) {
		return {
			widgetWidth: normalizedWidth,
			widgetHeight: normalizedHeight,
		};
	}

	const colUnit = defaultDivWidth + gap;
	const rowUnit = defaultDivHeight + gap;
	const cols = Math.max(1, Math.round((normalizedWidth + gap) / Math.max(1, colUnit)));
	const rows = Math.max(1, Math.round((normalizedHeight + gap) / Math.max(1, rowUnit)));

	return {
		widgetWidth: (cols * defaultDivWidth) + (Math.max(0, cols - 1) * gap),
		widgetHeight: (rows * defaultDivHeight) + (Math.max(0, rows - 1) * gap),
	};
};

export const findNearestEmptySpotForMetrics = ({
	startLeft,
	startTop,
	widgetWidth,
	widgetHeight,
	widgetsToCheck = [],
	metrics,
	defaultDivWidth,
	defaultDivHeight,
	getWidgetRectPx,
	toPxNumber,
	checkOverlap,
	subjectWidget = null,
	searchStep = null,
	preferredSpots = [],
	extraSearchHeight = 0,
}) => {
	if (!metrics) return { found: false, left: 0, top: 0 };

	const gap = metrics.gap || 0;
	const gridColumnSize = searchStep || defaultDivWidth + gap;
	const gridRowSize = searchStep || defaultDivHeight + gap;
	const ww = toPxNumber(widgetWidth, 0);
	const wh = toPxNumber(widgetHeight, 0);
	const minX = metrics.padLeft || 0;
	const maxX = Math.max(minX, (metrics.width || 0) - (metrics.padRight || 0) - ww);
	const minY = metrics.padTop || 0;
	const effectiveHeight = Math.max(metrics.height || 0, (metrics.height || 0) + Math.max(0, extraSearchHeight || 0));
	const maxY = Math.max(minY, effectiveHeight - (metrics.padBottom || 0) - wh);
	const usableWidth = metrics.usableWidth || 0;
	const usableHeight = Math.max(metrics.usableHeight || 0, effectiveHeight - (metrics.padTop || 0) - (metrics.padBottom || 0));
	const sx = toPxNumber(startLeft, 0);
	const sy = toPxNumber(startTop, 0);
	const maxCols = Math.max(0, Math.floor((usableWidth - ww) / Math.max(1, gridColumnSize)) + 1);
	const maxRows = Math.max(0, Math.floor((usableHeight - wh) / Math.max(1, gridRowSize)) + 1);
	const subjectType = typeof subjectWidget === 'string' ? subjectWidget : subjectWidget?.type;
	const blockedRects = (Array.isArray(widgetsToCheck) ? widgetsToCheck : [])
		.filter((widget) => {
			if (!widget) return false;
			if (widget.type === 'background-image' || widget.type === 'box-widget') return false;
			if (widget.type === STATUS_ARROW_WIDGET_TYPE || subjectType === STATUS_ARROW_WIDGET_TYPE) return false;
			if (canWaterWidgetsShareSpace(subjectWidget, widget)) return false;
			return true;
		})
		.map((widget) => {
			const rect = typeof getWidgetRectPx === 'function'
				? getWidgetRectPx(widget, metrics)
				: getWidgetRectPxForDuplication(widget, metrics, toPxNumber);
			return {
				left: rect.left,
				top: rect.top,
				right: rect.left + rect.width,
				bottom: rect.top + rect.height,
			};
		});

	const overlapsBlockedRect = (x, y) => {
		const right = x + ww;
		const bottom = y + wh;
		for (const rect of blockedRects) {
			if (
				x < rect.right &&
				right > rect.left &&
				y < rect.bottom &&
				bottom > rect.top
			) {
				return true;
			}
		}
		return false;
	};

	const isSpotAvailable = (x, y) => {
		if (x < minX || y < minY) return false;
		if (x > maxX || y > maxY) return false;
		return !(blockedRects.length > 0
			? overlapsBlockedRect(x, y)
			: checkOverlap(x, y, ww, wh, widgetsToCheck, null, subjectWidget));
	};

	const normalizedPreferredSpots = Array.isArray(preferredSpots) ? preferredSpots : [];
	for (const spot of normalizedPreferredSpots) {
		const x = toPxNumber(spot?.left, NaN);
		const y = toPxNumber(spot?.top, NaN);
		if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
		if (isSpotAvailable(x, y)) {
			return { found: true, left: x, top: y };
		}
	}

	let bestX = null;
	let bestY = null;
	let minDist = Infinity;

	for (let r = 0; r < maxRows; r++) {
		for (let c = 0; c < maxCols; c++) {
			const x = minX + (c * gridColumnSize);
			const y = minY + (r * gridRowSize);

			if (x < minX || y < minY) continue;
			if (x > maxX || y > maxY) continue;
			if (!isSpotAvailable(x, y)) continue;

			const d = (x - sx) * (x - sx) + (y - sy) * (y - sy);
			if (d < minDist) {
				minDist = d;
				bestX = x;
				bestY = y;
			}
		}
	}

	if (bestX === null) return { found: false, left: 0, top: 0 };
	return { found: true, left: bestX, top: bestY };
};

const getWidgetRectPxForDuplication = (widget, _metrics, toPxNumber) => ({
	left: toPxNumber(widget?.left, 0),
	top: toPxNumber(widget?.top, 0),
	width: toPxNumber(widget?.width, 0),
	height: toPxNumber(widget?.height, 0),
});

export const findNearestFreeformDuplicationSpot = (params) => (
	findNearestEmptySpotForMetrics({
		...params,
		searchStep: WATER_NETWORK_FREEFORM_SEARCH_STEP,
	})
);
